/* ============================================
   NIMBA FOODS — Review Display System
   Loads reviews from JSON, renders cards, handles
   sorting, photo upload, and structured data.
   ============================================ */

(function() {
  'use strict';

  // Config: set by Liquid snippet or inline on product page
  const CONFIG = window.NIMBA_REVIEWS_CONFIG || {
    asin: 'B0FHN8VVW9',
    dataUrl: 'data/reviews.json',
    productName: 'Raw Shea Butter',
    productUrl: 'https://nimbafoods.co/products/raw-african-shea-butter-1lb-pouch'
  };

  const REVIEWS_PER_PAGE = 6;
  const POLICY_SENSITIVE_REVIEW_TERMS = /\b(eczema|diaper rash|rash|psoriasis|dermatitis|acne|sunburn|burns?|inflammation|anti-inflammatory|heal(?:ed|ing)?|cure|treat(?:ed|s|ment)?)\b/i;
  let allReviews = [];
  let displayedCount = 0;
  let currentSort = 'newest';
  let selectedRating = 0;

  // ── Star SVG helper ───────────────────────────────────
  function starSVG(size) {
    return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="currentColor"><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></svg>`;
  }

  function starsHTML(rating, size) {
    let html = '';
    for (let i = 0; i < 5; i++) {
      const filled = i < Math.round(rating);
      html += `<span style="color:${filled ? 'var(--nb-gold)' : 'var(--nb-stone-300)'}">${starSVG(size || 14)}</span>`;
    }
    return html;
  }

  // ── Format reviewer name ──────────────────────────────
  function formatName(name) {
    if (!name) return 'Anonymous';
    // Already formatted like "Nancy R." — keep as-is
    if (/^[A-Z][a-z]+ [A-Z]\.$/.test(name)) return name;
    // Single word usernames — capitalize first letter
    const clean = name.trim();
    if (!clean.includes(' ') && !clean.includes('.')) {
      return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
    }
    return clean;
  }

  // ── Format date ───────────────────────────────────────
  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }

  // ── Compute aggregate stats ───────────────────────────
  function computeAggregate(reviews) {
    if (!reviews.length) return { avg: 0, count: 0, dist: [0,0,0,0,0] };
    const dist = [0, 0, 0, 0, 0];
    let sum = 0;
    reviews.forEach(r => {
      sum += r.star_rating;
      dist[r.star_rating - 1]++;
    });
    return {
      avg: (sum / reviews.length).toFixed(1),
      count: reviews.length,
      dist: dist
    };
  }

  // ── Render summary bar ────────────────────────────────
  function renderSummary(reviews) {
    const el = document.getElementById('reviewSummary');
    if (!el) return;
    const agg = computeAggregate(reviews);

    el.innerHTML = `
      <div class="pd-reviews__big-score">
        <div class="pd-reviews__number">${agg.avg}</div>
        <div class="pd-reviews__stars-big">${starsHTML(parseFloat(agg.avg), 16)}</div>
        <div class="pd-reviews__count">${agg.count} review${agg.count !== 1 ? 's' : ''}</div>
      </div>
      <div class="pd-reviews__bars">
        ${[5,4,3,2,1].map(star => {
          const pct = agg.count ? Math.round((agg.dist[star - 1] / agg.count) * 100) : 0;
          return `<div class="pd-reviews__bar-row">${star} <div class="pd-reviews__bar-track"><div class="pd-reviews__bar-fill" style="width:${pct}%"></div></div> ${pct}%</div>`;
        }).join('')}
      </div>
    `;
  }

  // ── Render a single review card ───────────────────────
  function renderCard(review) {
    const photos = review.photos || [];
    const photosHTML = photos.length ? `
      <div class="pd-reviews__card-photos">
        ${photos.map(p => `<img src="${p}" alt="Customer photo" class="pd-reviews__card-photo" onclick="window.NimbaReviews.openPhoto('${p}')" loading="lazy">`).join('')}
      </div>
    ` : '';

    return `
      <div class="pd-reviews__card pd-reveal">
        <div class="pd-reviews__card-stars">${starsHTML(review.star_rating)}</div>
        ${review.verified_purchase ? '<span class="pd-reviews__card-badge">Verified Purchase</span>' : ''}
        <h4 class="pd-reviews__card-title">&ldquo;${escapeHTML(review.review_title)}&rdquo;</h4>
        <p class="pd-reviews__card-text">${escapeHTML(review.review_body)}</p>
        ${photosHTML}
        <span class="pd-reviews__card-author">${escapeHTML(formatName(review.reviewer_name))} &mdash; ${formatDate(review.review_date)}</span>
      </div>
    `;
  }

  // ── Render review grid ────────────────────────────────
  function renderGrid() {
    const grid = document.getElementById('reviewGrid');
    const loadMore = document.getElementById('reviewLoadMore');
    if (!grid) return;

    const sorted = sortReviews(allReviews, currentSort);
    const toShow = sorted.slice(0, displayedCount);
    grid.innerHTML = toShow.map(renderCard).join('');

    // Re-observe dynamically added cards for scroll-reveal animation
    // (product.js observer only watches elements present at page load)
    grid.querySelectorAll('.pd-reveal').forEach(el => {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('pd-revealed');
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });
      obs.observe(el);
    });

    if (loadMore) {
      loadMore.style.display = displayedCount < sorted.length ? 'block' : 'none';
    }
  }

  // ── Sort reviews ──────────────────────────────────────
  function sortReviews(reviews, mode) {
    const copy = [...reviews];
    if (mode === 'highest') {
      copy.sort((a, b) => b.star_rating - a.star_rating || new Date(b.review_date) - new Date(a.review_date));
    } else {
      copy.sort((a, b) => new Date(b.review_date) - new Date(a.review_date));
    }
    return copy;
  }

  // ── Escape HTML ───────────────────────────────────────
  function escapeHTML(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function isPolicySafeReview(review) {
    const text = `${review.review_title || ''} ${review.review_body || ''}`;
    return !POLICY_SENSITIVE_REVIEW_TERMS.test(text);
  }

  // ── Sync hero star count with actual data ─────────────
  function updateHeroStars(reviews) {
    const link = document.getElementById('pdHeroReviewLink');
    if (!link || !reviews.length) return;
    const agg = computeAggregate(reviews);
    link.textContent = `${agg.avg} (${agg.count} review${agg.count !== 1 ? 's' : ''})`;
  }

  // ── Update JSON-LD structured data ────────────────────
  function updateStructuredData(reviews) {
    const el = document.getElementById('reviewStructuredData');
    if (!el) return;
    const agg = computeAggregate(reviews);
    if (!agg.count) return;

    // Parse existing rich schema (preserves SKU, manufacturer, shipping, offers)
    var schema;
    try { schema = JSON.parse(el.textContent); } catch (e) { return; }

    // Build individual review entries (up to 10 for SEO)
    var reviewEntries = reviews.slice(0, 10).map(function(r) {
      return {
        '@type': 'Review',
        'author': { '@type': 'Person', 'name': formatName(r.reviewer_name) },
        'datePublished': r.review_date,
        'reviewBody': r.review_body,
        'name': r.review_title,
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': r.star_rating,
          'bestRating': 5,
          'worstRating': 1
        }
      };
    });

    // Merge review data into existing schema (don't overwrite product details)
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': agg.avg,
      'reviewCount': agg.count,
      'bestRating': 5,
      'worstRating': 1
    };
    schema.review = reviewEntries;

    el.textContent = JSON.stringify(schema);
  }

  // ── Photo upload handling ─────────────────────────────
  let pendingPhotos = [];

  function handlePhotoSelect(e) {
    const files = Array.from(e.target.files);
    const preview = document.getElementById('reviewPhotoPreview');
    if (!preview) return;

    files.forEach(file => {
      if (!file.type.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) return; // 5MB max

      const reader = new FileReader();
      reader.onload = (evt) => {
        pendingPhotos.push(evt.target.result);
        const thumb = document.createElement('div');
        thumb.className = 'pd-reviews__photo-thumb';
        thumb.innerHTML = `
          <img src="${evt.target.result}" alt="Upload preview">
          <button type="button" class="pd-reviews__photo-remove" aria-label="Remove photo">&times;</button>
        `;
        thumb.querySelector('.pd-reviews__photo-remove').addEventListener('click', () => {
          const idx = pendingPhotos.indexOf(evt.target.result);
          if (idx > -1) pendingPhotos.splice(idx, 1);
          thumb.remove();
        });
        preview.appendChild(thumb);
      };
      reader.readAsDataURL(file);
    });
  }

  // ── Photo lightbox ────────────────────────────────────
  function openPhoto(src) {
    const lb = document.getElementById('reviewLightbox');
    const img = document.getElementById('reviewLightboxImg');
    if (lb && img) {
      img.src = src;
      lb.classList.add('pd-lightbox--open');
      document.body.style.overflow = 'hidden';
    }
  }

  // ── Handle review submission ──────────────────────────
  function handleSubmit(e) {
    e.preventDefault();
    const name = document.getElementById('reviewName').value.trim();
    const title = document.getElementById('reviewTitle').value.trim();
    const body = document.getElementById('reviewBody').value.trim();

    if (!name || !title || !body || !selectedRating) return;

    if (
      typeof window.ntrack === 'function' &&
      (/\/products\/raw-african-shea-butter/.test(window.location.pathname) || /product-raw\.html$/.test(window.location.pathname))
    ) {
      window.ntrack('pdp_raw_reviews_submit');
    }

    const newReview = {
      reviewer_name: name,
      star_rating: selectedRating,
      review_title: title,
      review_body: body,
      review_date: new Date().toISOString().split('T')[0],
      verified_purchase: false,
      photos: [...pendingPhotos],
      source: 'site'
    };

    // Store in localStorage (for preview). In production, POST to Shopify API or custom endpoint.
    const stored = JSON.parse(localStorage.getItem('nimba_reviews') || '[]');
    stored.push(newReview);
    localStorage.setItem('nimba_reviews', JSON.stringify(stored));

    // Add to live display
    allReviews.unshift(newReview);
    displayedCount = Math.min(displayedCount + 1, allReviews.length);
    renderSummary(allReviews);
    renderGrid();
    updateStructuredData(allReviews);

    // Reset form
    e.target.reset();
    selectedRating = 0;
    pendingPhotos = [];
    const preview = document.getElementById('reviewPhotoPreview');
    if (preview) preview.innerHTML = '';
    updateStarPicker(0);

    // Show success
    const success = document.getElementById('reviewFormSuccess');
    if (success) {
      success.style.display = 'block';
      setTimeout(() => { success.style.display = 'none'; }, 4000);
    }
  }

  // ── Star picker interaction ───────────────────────────
  function updateStarPicker(rating) {
    const buttons = document.querySelectorAll('#reviewStarPicker button');
    buttons.forEach((btn, i) => {
      btn.classList.toggle('active', i < rating);
    });
  }

  // ── Init ──────────────────────────────────────────────
  function init() {
    // Load reviews from JSON
    fetch(CONFIG.dataUrl)
      .then(r => r.json())
      .then(data => {
        // Find the product by ASIN
        const product = data.products.find(p => p.asin === CONFIG.asin);
        const amazonReviews = product ? product.reviews : [];

        // Also load locally-submitted reviews
        const localReviews = JSON.parse(localStorage.getItem('nimba_reviews') || '[]');

        allReviews = [...localReviews, ...amazonReviews].filter(isPolicySafeReview);
        displayedCount = Math.min(REVIEWS_PER_PAGE, allReviews.length);

        renderSummary(allReviews);
        renderGrid();
        updateStructuredData(allReviews);
        updateHeroStars(allReviews);
      })
      .catch((err) => {
        console.warn('[Nimba Reviews] Failed to load reviews JSON:', err);
        // If JSON fails to load, show local reviews only
        const localReviews = JSON.parse(localStorage.getItem('nimba_reviews') || '[]');
        allReviews = localReviews.filter(isPolicySafeReview);
        displayedCount = Math.min(REVIEWS_PER_PAGE, allReviews.length);
        renderSummary(allReviews);
        renderGrid();
      });

    // Sort toggle
    const sortSelect = document.getElementById('reviewSort');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        currentSort = e.target.value;
        displayedCount = Math.min(REVIEWS_PER_PAGE, allReviews.length);
        renderGrid();
      });
    }

    // Load more
    const loadMoreBtn = document.getElementById('reviewLoadMoreBtn');
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', () => {
        displayedCount = Math.min(displayedCount + REVIEWS_PER_PAGE, allReviews.length);
        renderGrid();
      });
    }

    // Photo upload
    const photoInput = document.getElementById('reviewPhotos');
    if (photoInput) {
      photoInput.addEventListener('change', handlePhotoSelect);
    }

    // Star picker
    const starPicker = document.getElementById('reviewStarPicker');
    if (starPicker) {
      starPicker.querySelectorAll('button').forEach(btn => {
        btn.addEventListener('click', () => {
          selectedRating = parseInt(btn.dataset.star);
          updateStarPicker(selectedRating);
        });
        btn.addEventListener('mouseenter', () => {
          updateStarPicker(parseInt(btn.dataset.star));
        });
      });
      starPicker.addEventListener('mouseleave', () => {
        updateStarPicker(selectedRating);
      });
    }

    // Form submit
    const form = document.getElementById('reviewForm');
    if (form) {
      form.addEventListener('submit', handleSubmit);
    }

    // Review lightbox close on backdrop click
    const lb = document.getElementById('reviewLightbox');
    if (lb) {
      lb.addEventListener('click', (e) => {
        if (e.target === lb) {
          lb.classList.remove('pd-lightbox--open');
          document.body.style.overflow = '';
        }
      });
    }
  }

  // ── Public API ────────────────────────────────────────
  window.NimbaReviews = {
    openPhoto: openPhoto
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
